import { convertConnectionToDetails } from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  TripDocumentWithCache,
  TripDocumentWithCacheSearch,
  SearchTripDocumentResponseTripDocumentWithCacheConnectionArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';
import { SearchTripDocument } from '@/features/Trips/api/queries/SearchTripDocument';

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof TripDocumentWithCacheSearch]?: any;
}[];
type ServiceMethodResponse = {
  documents: TripDocumentWithCache[];
  deletedDocuments: TripDocumentWithCache[];
};
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ServiceMethodResponse>;
}

// SearchTripDocument Service
export const { searchTripDocument, refetchTripDocument, useTripDocument } = ((): {
  searchTripDocument: ServiceMethod;
  refetchTripDocument: ServiceMethod;
  useTripDocument: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchTripDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ServiceMethodResponse> => {
        const args: SearchTripDocumentResponseTripDocumentWithCacheConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query: payload,
          },
        };
        const opts = { merge: options?.merge ? 'searchTripDocument.tripDocumentWithCacheConnection' : undefined };
        const res = await fn(args, opts);
        const connectionDetails = convertConnectionToDetails(getProperty('searchTripDocument.tripDocumentWithCacheConnection', res));

        const documents: TripDocumentWithCache[] = [];
        const deletedDocuments: TripDocumentWithCache[] = [];

        connectionDetails.rows.forEach((doc: TripDocumentWithCache) => {
          if (doc.deletedAt !== null && doc.deletedBy !== null) {
            deletedDocuments.push(doc);
          } else {
            documents.push(doc);
          }
        });

        return { documents, deletedDocuments };
      }
  );

  // Custom merge function for the new return structure
  const mergeDocuments = (res: ServiceMethodResponse, current: ServiceMethodResponse): ServiceMethodResponse => ({
    documents: [...(current?.documents || []), ...res.documents],
    deletedDocuments: [...(current?.deletedDocuments || []), ...res.deletedDocuments],
  });

  const useService = createGraphApiHook(fetch, { refetch, merge: mergeDocuments });
  return {
    searchTripDocument: fetch,
    refetchTripDocument: refetch,
    useTripDocument: useService,
  };
})();
